import {
	Button,
	ColumnHeader,
	DateUtil,
	Dropdown,
	DropdownMenu,
	DropdownMenuItem,
	DropdownToggle,
	Icon,
	Table,
	TableColumn,
	TableRow,
} from '@aon/aon-tws-common-ui';
import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { EmailTemplateType, IAction, IAudience, IColumn, IEmailTemplate, IValidationErrorCollection } from '../../../models';
import { ColumnHelper } from '../../../utilities/ColumnHelper';
import { Col3Col9, WarningModal } from '../../common';
import { ToastType } from '../ToastType';
import { ReminderEmailModal } from './ReminderEmailModal';
import { SendEmailModal } from './SendEmailModal';

const ReminderEmailContainer = styled.div`
	padding-top: 40px;
	padding-bottom: 20px;
	.tws-dropdown-menuitem {
		text-align: left;
	}
`;
const ReminderTableContainer = styled.div`
	padding-top: 40px;
`;
const WarningMessageLabel = styled.div`
	color: red;
	& [class^='icon-'] {
		margin-right: 5px;
	}
`;
WarningMessageLabel.displayName = 'WarningMessageLabel';

const defaultEmailTemplate: IEmailTemplate = {
	emailTemplateId: 0,
	audienceId: 0,
	emailTemplateName: '',
	subject: '',
	content: '',
	invitationType: EmailTemplateType.Reminder,
	createdOn: undefined,
	scheduledFor: undefined,
	modifiedOn: undefined,
	uniqueId: _.uniqueId(),
	action: IAction.Insert,
};

export interface IReminderEmailsProps {
	audience: IAudience;
	columns: IColumn[];
	invitationEmail: IEmailTemplate;
	shouldDisableCreateReminder: boolean;
	reminderEmails: IEmailTemplate[];
	reminderEmailValidationErrors?: IValidationErrorCollection;
	addReminderToList: (emailTemplate: IEmailTemplate) => any;
	addReminderEmailValidationErrors: (validationError?: IValidationErrorCollection) => any;
	deleteReminder: (emailTemplate: IEmailTemplate) => any;
	toast: (type: ToastType, message: string) => any;
}

export class ReminderEmails extends React.Component<
	IReminderEmailsProps,
	{
		reminderEmailModalOpen: boolean;
		reminderEmail: IEmailTemplate;
		x: number;
		y: number;
		warningModalOpen: boolean;
		pendingDeleteReminder?: IEmailTemplate;
		sendEmailModalOpen: boolean;
		pendingSendEmailReminder?: IEmailTemplate;
	}
> {
	constructor(props: IReminderEmailsProps) {
		super(props);

		this.state = {
			reminderEmailModalOpen: false,
			reminderEmail: { ...defaultEmailTemplate, audienceId: this.props.audience.audienceId, uniqueId: _.uniqueId() },
			x: 0,
			y: 0,
			warningModalOpen: false,
			sendEmailModalOpen: false,
		};

		this.openReminderEmailModal = this.openReminderEmailModal.bind(this);
		this.closeReminderEmailModal = this.closeReminderEmailModal.bind(this);
		this.onEdit = this.onEdit.bind(this);
		this.onDelete = this.onDelete.bind(this);
		this.openSendEmailModal = this.openSendEmailModal.bind(this);
		this.closeSendEmailModal = this.closeSendEmailModal.bind(this);
	}

	public render() {
		const { audience, columns, reminderEmails, toast } = this.props;
		const { reminderEmail, warningModalOpen, pendingSendEmailReminder, pendingDeleteReminder, sendEmailModalOpen, reminderEmailModalOpen } =
			this.state;

		const notDeletedReminderEmails: IEmailTemplate[] = _.filter(reminderEmails, (et: IEmailTemplate) => {
			return et.action !== IAction.Delete;
		});
		let contentBody = ContentConstants.WARNING_MODAL_DELETE_BODY;
		if (pendingDeleteReminder) {
			contentBody = _.replace(contentBody, new RegExp('{name}', 'g'), pendingDeleteReminder.emailTemplateName!);
		}

		return (
			<ReminderEmailContainer>
				<Col3Col9 label={ContentConstants.REMINDER_EMAIL_SECTION_TITLE} labelSize='lg' className='tws-reminder-emails'>
					<p>
						<strong>{ContentConstants.INSTRUCTIONALTEXT_REMINDER_EMAIL_NOTE}</strong>
					</p>
					<Button
						id='createReminderEmail'
						size='sm'
						onClick={this.openReminderEmailModal}
						color='secondary'
						disabled={this.props.shouldDisableCreateReminder}
					>
						{ContentConstants.REMINDER_EMAIL_CREATE_NEW}
					</Button>
					{notDeletedReminderEmails && notDeletedReminderEmails.length > 0 && (
						<ReminderTableContainer>
							<Table lastalign='right'>
								<ColumnHeader columns={ColumnHelper.GetColumnHeaders(columns)} />
								<tbody>
									{notDeletedReminderEmails.map((re, index) => {
										return (
											<TableRow key={`reminder-row-${index}`}>
												<TableColumn>
													{re.emailTemplateName}
													{!this.validateReminderDate(re.scheduledFor) && (
														<WarningMessageLabel className='tws-reminder-email-date-warning-message'>
															<Icon name='warning' />
															{ContentConstants.WARNING_REMINDER_EMAILS_SCHEDULEDFOR}
														</WarningMessageLabel>
													)}
												</TableColumn>
												<TableColumn>{re.scheduledFor ? DateUtil.formatDateToLocal(new Date(re.scheduledFor)) : ''}</TableColumn>
												<TableColumn>
													<Dropdown>
														<DropdownToggle>
															<a>
																<Icon name='more-info' />
															</a>
														</DropdownToggle>
														<DropdownMenu x={this.state.x - 160} y={this.state.y - 10}>
															<DropdownMenuItem onClick={() => this.onEdit(re)}>{ContentConstants.ACTIONMENU_EDIT}</DropdownMenuItem>
															<DropdownMenuItem onClick={() => this.onDelete(re)}>{ContentConstants.ACTIONMENU_DELETE}</DropdownMenuItem>
															<DropdownMenuItem onClick={() => this.openSendEmailModal(re)}>
																{ContentConstants.BUTTONS_SEND_TEST_EMAIL}
															</DropdownMenuItem>
														</DropdownMenu>
													</Dropdown>
												</TableColumn>
											</TableRow>
										);
									})}
								</tbody>
							</Table>
						</ReminderTableContainer>
					)}

					<ReminderEmailModal
						audience={audience}
						reminderEmail={reminderEmail}
						validationErrors={this.props.reminderEmailValidationErrors}
						modalOpen={reminderEmailModalOpen}
						addReminderToList={this.props.addReminderToList}
						addReminderEmailValidationErrors={this.props.addReminderEmailValidationErrors}
						closeModal={this.closeReminderEmailModal}
						reminderEmails={notDeletedReminderEmails}
						toast={toast}
					/>

					{warningModalOpen && pendingDeleteReminder && (
						<WarningModal
							isOpen={warningModalOpen}
							onClose={() => this.closeWarningModal()}
							onSubmit={this.onSubmit}
							contentHeadline={ContentConstants.WARNING_MODAL_DELETE_HEADLINE}
							contentBody={contentBody}
						/>
					)}

					{sendEmailModalOpen && pendingSendEmailReminder && (
						<SendEmailModal
							modalOpen={sendEmailModalOpen}
							emailTemplate={pendingSendEmailReminder}
							shouldReplaceSurveyLink={false}
							closeModal={this.closeSendEmailModal}
						/>
					)}
				</Col3Col9>
			</ReminderEmailContainer>
		);
	}

	private openReminderEmailModal() {
		const { audience, invitationEmail } = this.props;

		this.setState(
			{
				reminderEmailModalOpen: true,
				reminderEmail: {
					...defaultEmailTemplate,
					from: invitationEmail.from,
					replyToEmail: invitationEmail.replyToEmail,
					subject: invitationEmail.subject,
					content: invitationEmail.content,
					audienceId: audience.audienceId,
					enableHtmlContent: invitationEmail.enableHtmlContent,
					uniqueId: _.uniqueId(),
				},
			},
			() => {
				this.props.addReminderEmailValidationErrors(undefined);
			},
		);
	}

	private onEdit = (reminder: IEmailTemplate) => {
		this.setState({ reminderEmailModalOpen: true, reminderEmail: { ...reminder, action: IAction.Update } }, () => {
			this.props.addReminderEmailValidationErrors(undefined);
		});
	};

	private onDelete = (reminder: IEmailTemplate) => {
		this.setState({ warningModalOpen: true, pendingDeleteReminder: reminder });
	};

	private onSubmit = () => {
		this.setState({ warningModalOpen: false }, () => {
			this.props.deleteReminder(this.state.pendingDeleteReminder!);
		});
	};

	private openSendEmailModal(reminder: IEmailTemplate) {
		this.setState({ sendEmailModalOpen: true, pendingSendEmailReminder: reminder });
	}

	private closeSendEmailModal() {
		this.setState({ sendEmailModalOpen: false, pendingSendEmailReminder: undefined });
	}

	private closeWarningModal() {
		this.setState({ warningModalOpen: false, pendingDeleteReminder: undefined });
	}

	private closeReminderEmailModal() {
		this.setState({ reminderEmailModalOpen: false });
	}

	private validateReminderDate(date: any) {
		const { audience } = this.props;

		let isValid = true;
		if (audience.closeOn) {
			const closeOn = moment(audience.closeOn);
			let updatedOpenOn = moment(audience.openOn);
			if (audience.openImmediately) {
				const currentDate = moment();
				currentDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
				updatedOpenOn = currentDate;
			}
			const scheduledFor = moment(date);
			if (scheduledFor.valueOf() < updatedOpenOn.valueOf() || scheduledFor.valueOf() > closeOn.valueOf()) {
				isValid = false;
				return isValid;
			}
		}
		return isValid;
	}
}
